/**
 * Sorting utility for analytics chart labels that encode dates.
 *
 * The backend produces time-series labels via AnalyticsEngine.formatDateLabel:
 * - DAY: "2026-01-25"
 * - WEEK: "Semana 2,\nEnero del 26"
 * - MONTH: "ene. 2026" (Spanish, may vary with/without dot)
 * - QUARTER: "Q1 2026"
 * - YEAR: "2026"
 *
 * Without date-aware sorting, localeCompare orders these alphabetically
 * ("abr." < "ene." < "oct.") which scrambles chronological order and mixes
 * years. Non-date labels keep the localeCompare fallback so categorical
 * charts are unaffected.
 */

const SPANISH_MONTHS = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

const ENGLISH_MONTHS = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
];

/**
 * Parses an analytics date label into a representative Date, or null when the
 * label is not a recognized date format.
 */
export function parseAnalyticsDateLabel(label: string): Date | null {
  if (!label) return null;

  // WEEK: "Semana 2,\nEnero del 26"
  const week = parseWeekLabel(label);
  if (week) return week;

  // QUARTER: "Q1 2026"
  const quarter = label.trim().match(/^Q([1-4])\s+(\d{4})$/i);
  if (quarter) {
    return new Date(Number(quarter[2]), (Number(quarter[1]) - 1) * 3, 1);
  }

  // MONTH: "ene. 2026" | "ene 2026" | "Jan 2026"
  const month = label.trim().match(/^([a-zA-Zñ]{3,4})\.?\s+(\d{4})$/);
  if (month) {
    const monthIdx = monthIndex(month[1]);
    if (monthIdx !== -1) {
      return new Date(Number(month[2]), monthIdx, 1);
    }
  }

  // YEAR: "2026"
  if (/^\d{4}$/.test(label.trim())) {
    return new Date(Number(label.trim()), 0, 1);
  }

  // DAY: "2026-01-25" (parse components directly; new Date(string) is UTC and
  // shifts the day in local timezones)
  const day = label.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (day) {
    return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  }

  return null;
}

/** Sorts items by their date labels when both parse as dates; otherwise falls back to localeCompare. */
export function sortByDateLabel<T extends { label: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const dateA = parseAnalyticsDateLabel(a.label);
    const dateB = parseAnalyticsDateLabel(b.label);

    if (dateA && dateB) return dateA.getTime() - dateB.getTime();
    if (dateA) return -1;
    if (dateB) return 1;
    return a.label.localeCompare(b.label);
  });
}

function parseWeekLabel(label: string): Date | null {
  if (!label.includes('Semana')) return null;

  const lines = label.split('\n');
  if (lines.length < 2) return null;

  const weekMatch = lines[0].match(/Semana (\d+)/);
  const monthYearMatch = lines[1].match(/(\w+) del (\d+)/);
  if (!weekMatch || !monthYearMatch) return null;

  const weekNum = parseInt(weekMatch[1], 10);
  const monthIdx = monthIndex(monthYearMatch[1]);
  const year = 2000 + parseInt(monthYearMatch[2], 10);

  if (monthIdx === -1) return null;

  // Approximate position within the month for ordering
  return new Date(year, monthIdx, weekNum * 7);
}

function monthIndex(prefix: string): number {
  const normalized = prefix.toLowerCase().replace(/\.$/, '');
  if (normalized.length < 3) return -1;
  const three = normalized.slice(0, 3);
  const spanish = SPANISH_MONTHS.indexOf(three);
  if (spanish !== -1) return spanish;
  return ENGLISH_MONTHS.indexOf(three);
}
